import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getWidget, listEntities } from '../../../services';
import { getDataset } from '../../../services/datasets.js';

/**
 * Safety ceiling on how many pages `listAllEntities` will walk. The catalog
 * pages at 100, so this is 5,000 entities — comfortably above any real
 * tenant's — and it exists only so a pagination bug on the backend cannot turn
 * this into a very long loop.
 */
const MAX_ENTITY_PAGES = 50;

/**
 * `listEntities()` alone only sees the first 100 rows: fine for the Data step's
 * own picker, but wrong for `withResolvedEntity` below — a widget's composite
 * is not more likely to be in the first 100 than any other, and one that is not
 * leaves the edit page with no entity and no columns.
 *
 * **Walks by cursor.** The catalog is keyset-paginated: it hands back
 * `meta.nextCursor` and that is the only thing that advances it. Asking for
 * `page: 2` returns page one again, with `hasNext: true` and the same cursor,
 * so a loop driven by page numbers and `hasNext` re-reads the first 100 rows
 * until it hits the ceiling — fifty round trips and five thousand duplicate
 * rows. That is what this used to do, and it cost the edit page forty seconds
 * of skeleton before it resolved nothing.
 *
 * A cursor that repeats is therefore still treated as the end: it means the
 * backend did not move, and trusting it would bring the same loop back.
 */
export const listAllEntities = async () => {
  const all = [];
  let cursor;
  const seen = new Set();

  for (let page = 0; page < MAX_ENTITY_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop -- keyset paging is sequential
    // by definition: the next request needs the previous response's cursor.
    const { entities, meta } = await listEntities({ nextCursor: cursor });
    all.push(...entities);

    cursor = meta?.nextCursor;
    if (!cursor || seen.has(cursor)) break;
    seen.add(cursor);
  }
  return all;
};

/**
 * Fill in the entity whose fields back the widget's composite.
 *
 * Returns the widget untouched when there is nothing to resolve, so the common
 * case — a widget saved through the builder, which records its member — costs
 * nothing and keeps its identity.
 *
 * **One composite, one row.** The catalog now lists composites, each carrying
 * its own `primaryEntityUuid`, so a composite uuid resolves to exactly one
 * entity. It used to list members and ask which of them belonged to the
 * composite: several usually did, "several" was indistinguishable from "wrong
 * one", so it gave up and left the Data step blank — on 17 of the 30 widgets
 * in dev.
 */
export const withResolvedEntity = (widget, entities) => {
  const config = widget?.builderConfig;
  if (!config?.compositeEntityUuid || config.entityRef) return widget;

  const member = entities.find(
    (entity) => entity.compositeEntityUuid === config.compositeEntityUuid,
  );
  if (!member) return widget;

  return {
    ...widget,
    builderConfig: {
      ...config,
      sourceId: member.primaryEntityUuid,
      entityRef: member.primaryEntityUuid,
      // The saved label is often empty on these, and the raw composite uuid is
      // what the preview's meta line was printing instead.
      sourceLabel: config.sourceLabel || member.name,
    },
  };
};

/**
 * Fill in the columns the widget's dataset exposes (CT-5725).
 *
 * Since the Data step publishes its selection as a DATASET, the record set
 * belongs to the dataset and not to the widget: a dataset-backed definition
 * comes back with `entity.recordSet: null` and an `entity` the engine derived
 * from the bindings. So `Columnas a exponer` reopened showing only the columns
 * some binding happened to name — five of fifteen on a table, **none at all**
 * on a chart, whose bindings are measures. Saving from there would publish a
 * narrower dataset than the one the widget was built on.
 *
 * The dataset is the source of truth and the widget stores its uuid, so this
 * reads them back. A published dataset is never edited — a different selection
 * is a different dataset — so what it answers is what the widget was built on.
 *
 * The row key is left out: the backend adds it to every dataset and the Data
 * step does not offer it.
 *
 * **The dataset wins over what the definition carries.** For a dataset-backed
 * widget the `recordSet` on the definition is the engine's derivation from the
 * bindings, not the author's selection — a table came back with the five
 * columns it draws out of the fifteen it was built on. There is no dataset to
 * read for an `ENTITY` widget, so its own record set is left alone.
 *
 * Returns the widget untouched when there is nothing to read or nothing would
 * change: a reopened widget must not start out dirty.
 *
 * ## Three things come back from here, not one (CT-5860)
 *
 * The dataset is not just the column list. A widget built on one stores
 * nothing else about its source — `dataSource` carries `queryUuid` and no
 * `entity` at all — so the read has to answer for the whole Data step:
 *
 * - **the exposed columns**, as above;
 * - **the composite the dataset was built from**, which the definition never
 *   carries in this mode. Without it the entity picker opened with nothing
 *   marked, and a save was refused outright:
 *   `DSW001 dataSource.entity.compositeEntityUuid is required`;
 * - **the dataset itself, as `datasetRef`**, which is what tells the payload
 *   builder it is on a dataset. That flag used to live only in the state
 *   `usePublishedDataset` filled when the author pressed `Continue`, so after
 *   a reload the next save fell back to `mode: ENTITY` — a shape the Data
 *   step no longer feeds — and wrote `x: { field_key: 'dim_1' }` over
 *   bindings that named real columns.
 *
 * `signature` is deliberately absent: it describes a selection this session
 * has not made, so the next `ensureDataset` asks again. `create` is
 * idempotent on the selection, so that answers with this same dataset unless
 * the author changed something, which is exactly when a new one is right.
 */
const sameColumns = (a, b) => Array.isArray(a)
  && a.length === b.length
  && a.every((value, i) => value === b[i]);

export const withDataset = (widget, dataset) => {
  const config = widget?.builderConfig;
  if (!config || !dataset?.datasetUuid) return widget;

  const columns = (dataset.columns ?? [])
    .filter((column) => !column.isRowKey)
    .map((column) => column.fieldUuid)
    .filter(Boolean);

  const takesColumns = Boolean(columns.length) && !sameColumns(config.exposedColumns, columns);
  // The definition's own value wins when it has one: an `ENTITY` widget names
  // its composite, and only a dataset-backed one arrives without it.
  const takesComposite = !config.compositeEntityUuid && Boolean(dataset.compositeEntityUuid);

  return {
    ...widget,
    datasetRef: dataset,
    // Kept by reference when neither applies: the form takes its baseline
    // from this object, and a copy of it would read as unsaved work.
    builderConfig: takesColumns || takesComposite
      ? {
        ...config,
        ...(takesColumns ? { exposedColumns: columns } : {}),
        ...(takesComposite ? { compositeEntityUuid: dataset.compositeEntityUuid } : {}),
      }
      : config,
  };
};

/**
 * Load one widget for the Playground's edit mode (CT-5190 / DS-6).
 *
 * Goes through the `getWidget` service, which reads the **published** version
 * of the definition — the one the library lists — and turns it into builder
 * state. The service already validates the envelope and maps the definition, so
 * a 404 arrives here as a throw carrying `response.status`.
 *
 * `notFound` is separated from `error` on purpose: a 404 means "this widget is
 * gone, here is the way back to the catalog", while a failed envelope means
 * "try again". They are different states with different exits, and collapsing
 * them would show a retry button that can never succeed.
 *
 * ## Resolving the entity behind the composite
 *
 * The definition stores the **composite** the widget reads from, and
 * `field/list` does not answer for a composite uuid. Which entity to ask
 * instead is only recorded in `visualizationConfig.memberEntityUuid`, so a
 * widget created through the API — or saved before that field was written —
 * arrives with the composite and nothing else, and the Data step opened with
 * no entity marked and no columns.
 *
 * The catalog answers that directly now: the composite's row carries its
 * `primaryEntityUuid`, which is both the one entity whose fields the widget
 * endpoint accepts and the one this looks up.
 *
 * It happens here, before the form takes its baseline, so a widget opened for
 * edit does not start out dirty for something the user did not touch.
 *
 * @param {string} widgetId
 */
export const useWidgetForEdit = (widgetId) => {
  const [state, setState] = useState({ widget: null, error: null, notFound: false });
  const [resolvedKey, setResolvedKey] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const fetchIdRef = useRef(0);

  const requestKey = `${widgetId ?? ''}|${reloadToken}`;
  const isLoading = resolvedKey !== requestKey;

  useEffect(() => {
    if (!widgetId) return undefined;

    const fetchId = ++fetchIdRef.current;
    let mounted = true;

    Promise.all([
      getWidget(widgetId),
      // Failing to read the catalogue must not fail the edit: without it the
      // member simply stays unresolved, which is where we were before.
      listAllEntities().catch(() => []),
    ])
      .then(async ([widget, entities]) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        const resolved = withResolvedEntity(widget, entities);
        /*
          Sequential on purpose: which dataset to read is the widget's own
          answer. Failing to read it must not fail the edit either — the Data
          step then opens with the columns the definition named, which is
          where we were before.
        */
        const datasetUuid = resolved?.builderConfig?.datasetId;
        const dataset = datasetUuid ? await getDataset(datasetUuid).catch(() => null) : null;
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({
          widget: withDataset(resolved, dataset),
          error: null,
          notFound: false,
        });
      })
      .catch((err) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        const notFound = err?.response?.status === 404;
        setState({ widget: null, error: notFound ? null : err, notFound });
      })
      .finally(() => {
        if (mounted && fetchId === fetchIdRef.current) setResolvedKey(requestKey);
      });

    return () => {
      mounted = false;
    };
  }, [requestKey, widgetId]);

  const retry = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  return useMemo(
    () => ({
      widget: state.widget,
      isLoading,
      error: state.error,
      notFound: state.notFound,
      retry,
    }),
    [isLoading, retry, state.error, state.notFound, state.widget],
  );
};

export default useWidgetForEdit;
